/**
 * AP bank-clearing lag after Billing & Payables "Mark paid".
 * Shared by Forecast page and financeAgentApi cashForecast / apByMonth.
 *
 * Priority:
 * 1. QuickBooks bill payment cleared the bank (qb cleared / deposit date)
 * 2. Else paid date + lag by payment method (Finance settings, with defaults)
 */

export type BillPayClearingMethod = 'ach' | 'check' | 'card' | 'other';

export type BillPayClearingSettings = {
  achBusinessDays: number;
  checkBusinessDays: number;
  /** When set, card clears on this calendar day-of-month after the paid month (1–28). */
  cardDueDayOfMonth: number | null;
};

export const DEFAULT_BILL_PAY_CLEARING: BillPayClearingSettings = {
  achBusinessDays: 2,
  checkBusinessDays: 7,
  cardDueDayOfMonth: null,
};

export function parseBillPayClearingSettings(raw: unknown): BillPayClearingSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_BILL_PAY_CLEARING };
  const row = raw as Record<string, unknown>;
  const ach = Number(row.achBusinessDays);
  const check = Number(row.checkBusinessDays);
  const cardDay = Number(row.cardDueDayOfMonth);
  return {
    achBusinessDays: Number.isFinite(ach) && ach >= 0 ? Math.min(30, Math.floor(ach)) : DEFAULT_BILL_PAY_CLEARING.achBusinessDays,
    checkBusinessDays: Number.isFinite(check) && check >= 0 ? Math.min(45, Math.floor(check)) : DEFAULT_BILL_PAY_CLEARING.checkBusinessDays,
    cardDueDayOfMonth: Number.isFinite(cardDay) && cardDay >= 1 && cardDay <= 28
      ? Math.floor(cardDay)
      : null,
  };
}

export function inferBillPayMethod(raw: string | null | undefined): BillPayClearingMethod {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return 'ach';
  if (/check|cheque|ck\b/.test(s)) return 'check';
  if (/card|visa|master|amex|credit/.test(s)) return 'card';
  if (/ach|wire|eft|transfer|bank/.test(s)) return 'ach';
  return 'other';
}

/** Add N US business days (Mon–Fri); skips Sat/Sun only. */
export function addBusinessDays(from: Date, businessDays: number): Date {
  const out = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let left = Math.max(0, Math.floor(businessDays));
  while (left > 0) {
    out.setDate(out.getDate() + 1);
    const dow = out.getDay();
    if (dow !== 0 && dow !== 6) left -= 1;
  }
  return out;
}

function cardClearingDate(paidAt: Date, dayOfMonth: number | null): Date {
  if (dayOfMonth == null) {
    // Fallback: next statement-ish — 25th of following month.
    return new Date(paidAt.getFullYear(), paidAt.getMonth() + 1, 25);
  }
  const candidate = new Date(paidAt.getFullYear(), paidAt.getMonth(), dayOfMonth);
  if (candidate.getTime() <= paidAt.getTime()) {
    return new Date(paidAt.getFullYear(), paidAt.getMonth() + 1, dayOfMonth);
  }
  return candidate;
}

export type BillClearingInput = {
  status?: string | null;
  paidAt?: Date | null;
  paymentMethod?: string | null;
  /** QuickBooks BillPayment / bank clear date when known. */
  qbClearedAt?: Date | null;
  qbPaymentDepositDate?: Date | null;
};

/**
 * Date the paid bill should leave forecast cash. Until then it stays as
 * clearing AP (still cash-out). Returns null when the bill is not paid.
 */
export function resolveBillClearingDate(
  bill: BillClearingInput,
  settings: BillPayClearingSettings = DEFAULT_BILL_PAY_CLEARING,
): Date | null {
  if (String(bill.status || '') !== 'paid') return null;
  const qbClear = bill.qbClearedAt || bill.qbPaymentDepositDate || null;
  if (qbClear && !Number.isNaN(qbClear.getTime())) {
    return new Date(qbClear.getFullYear(), qbClear.getMonth(), qbClear.getDate());
  }
  const paidAt = bill.paidAt;
  if (!paidAt || Number.isNaN(paidAt.getTime())) return null;
  const method = inferBillPayMethod(bill.paymentMethod);
  if (method === 'check') return addBusinessDays(paidAt, settings.checkBusinessDays);
  if (method === 'card') return cardClearingDate(paidAt, settings.cardDueDayOfMonth);
  // ach + other
  return addBusinessDays(paidAt, settings.achBusinessDays);
}

/** True once the bank clear date is on or before `now` (start of local day). */
export function isBillBankCleared(
  bill: BillClearingInput,
  now: Date,
  settings: BillPayClearingSettings = DEFAULT_BILL_PAY_CLEARING,
): boolean {
  if (String(bill.status || '') !== 'paid') return false;
  const clear = resolveBillClearingDate(bill, settings);
  if (!clear) return true; // paid with no dates — treat as cleared (legacy)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return clear.getTime() <= today.getTime();
}

/** Paid but still in forecast until clearing — "clearing" AP. */
export function isBillInClearing(
  bill: BillClearingInput,
  now: Date,
  settings: BillPayClearingSettings = DEFAULT_BILL_PAY_CLEARING,
): boolean {
  if (String(bill.status || '') !== 'paid') return false;
  return !isBillBankCleared(bill, now, settings);
}
